import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import slider1 from '../images/ATC_img2_accueil_1920x785.webp';
import slider2 from '../images/ATC_img1_accueil_1920x785.webp';
import formationImg from '../images/formation_450x500_1920x780.webp';
import './Slider.css';

const Slider = ({ featuresRef }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const intervalRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const slides = [
    {
      id: 0,
      image: slider1,
      title: 'ATC,',
      subtitle: [
        { text: t('slogan1_Msg1'), color: '#ffffff' },
        { text: t('slogan1_Msg2'), color: '#90ee90' }
      ]
    },
    {
      id: 1,
      image: slider2,
      title: t('slogan2_Msg1'),
      subtitle: [
        { text: t('slogan2_Msg2'), color: '#007bff' },
        { text: t('slogan2_Msg3'), color: '#ffffff' }
      ]
    },
    {
      id: 2,
      image: formationImg,
      title: t('slogan3_Msg1'),
      subtitle: [
        { text: t('slogan3_Msg2'), color: '#90ee90' },
        { text: t('slogan3_Msg3'), color: '#ffffff' }
      ]
    }
  ];

  // Fonction pour démarrer l'intervalle (avec réinitialisation)
  const startInterval = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!isHovered) {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    }, 6000);
  }, [slides.length, isHovered]);

  // Démarrer l'intervalle au montage et le nettoyer
  useEffect(() => {
    startInterval();
    return () => clearInterval(intervalRef.current);
  }, [startInterval]);

  // Réinitialiser l'intervalle quand isHovered change (pause/reprise)
  useEffect(() => {
    startInterval();
  }, [isHovered, startInterval]);

  // Fonctions de navigation avec réinitialisation
  const goToSlide = (index) => {
    setCurrentSlide(index);
    startInterval();
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    startInterval();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    startInterval();
  };

  // Variants d'animation
  const textVariants = {
    hidden: { opacity: 0, y: 60, rotateX: -15, perspective: 800 },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      transition: { duration: 0.7, ease: [0.6, -0.05, 0.01, 0.99] }
    },
    exit: {
      opacity: 0,
      y: -40,
      rotateX: 15,
      transition: { duration: 0.5 }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, x: -80, rotateY: -25, perspective: 800 },
    visible: {
      opacity: 1,
      x: 0,
      rotateY: 0,
      transition: { duration: 0.8, ease: [0.6, -0.05, 0.01, 0.99], delay: 0.2 }
    },
    exit: {
      opacity: 0,
      x: 60,
      rotateY: 20,
      transition: { duration: 0.5 }
    }
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      boxShadow: '0 0 30px rgba(0, 123, 255, 0.6)',
      transition: { duration: 0.2, yoyo: Infinity }
    },
    tap: { scale: 0.95 }
  };

  const scrollToFeatures = () => {
    if (!featuresRef?.current) return;

    const header = document.querySelector(".main-header");

    const headerHeight = header
        ? header.getBoundingClientRect().height
        : 0;

    const top =
        featuresRef.current.getBoundingClientRect().top +
        window.pageYOffset -
        headerHeight;

    window.scrollTo({
        top,
        behavior: "smooth",
    });
};

  return (
    <section 
      className="slider-section"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="grid-background"></div>
      <div className="particles-container">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            width: `${Math.random() * 4 + 2}px`,
            height: `${Math.random() * 4 + 2}px`
          }} />
        ))}
      </div>

      <div className="slider-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="slider-slide"
            style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.05 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          >
            <div className="slider-overlay"></div>
            <div className="slider-content">
              <motion.div
                className="slider-text"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={{
                  visible: { transition: { staggerChildren: 0.25 } }
                }}
              >
                <motion.h2 variants={titleVariants}>
                  {slides[currentSlide].title}
                </motion.h2>
                <motion.h3 variants={textVariants}>
                  {slides[currentSlide].subtitle.map((part, i) => {
                    const isLast = i === slides[currentSlide].subtitle.length - 1;
                    return (
                      <span
                        key={i}
                        style={{
                          color: part.color,
                          display: isLast ? 'block' : 'inline-block',
                          marginTop: isLast ? '8px' : '0'
                        }}
                      >
                        {part.text}
                      </span>
                    );
                  })}
                </motion.h3>
                <motion.div className="slider-buttons" variants={textVariants}>
                  <motion.a
                      href="#solutech_features_area"
                      className="btn-primary"
                      onClick={(e)=>{
                          e.preventDefault();
                          scrollToFeatures();
                      }}
                  >
                    {t('btn_Decouvrir_plus')}
                    <span className="btn-glow"></span>
                  </motion.a>
                  <motion.a
                    href="/contact"
                    className="btn-secondary"
                    variants={buttonVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    {t('btn_Nous_Contacter')}
                    <span className="btn-glow"></span>
                  </motion.a>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        <button className="slider-nav prev" onClick={prevSlide}>
          <i className="fas fa-chevron-left"></i>
        </button>
        <button className="slider-nav next" onClick={nextSlide}>
          <i className="fas fa-chevron-right"></i>
        </button>
        <div className="slider-dots">
          {slides.map((_, index) => (
            <span
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Slider;